import React, { useState, useCallback, memo } from 'react'
import { Box, Flex } from '@chakra-ui/core'
import { Editor } from '@tiptap/react'
import {
  Bold,
  Italic,
  Underline as UnderlineIcon,
  Strikethrough,
  Heading1,
  Heading2,
  Heading3,
  List,
  ListOrdered,
  Undo,
  Redo,
  Highlighter,
  Type,
} from 'lucide-react'
import { ColorPicker } from './ColorPicker'

interface ToolbarProps {
  editor: Editor
  isRtl?: boolean
}

interface ToolbarButtonProps {
  onClick?: () => void
  isActive?: boolean
  disabled?: boolean
  title?: string
  children: React.ReactNode
}

/**
 * ToolbarButton - memoized toolbar button
 */
const ToolbarButton = memo<ToolbarButtonProps>(({ onClick, isActive = false, disabled = false, title, children }) => (
  <button
    onClick={onClick}
    disabled={disabled}
    title={title}
    className={`toolbar-button ${isActive ? 'active' : ''}`}
    type="button"
  >
    {children}
  </button>
))

ToolbarButton.displayName = 'ToolbarButton'

/**
 * ToolbarDivider - toolbar separator
 */
const ToolbarDivider = memo(() => <Box className="toolbar-divider" />)

ToolbarDivider.displayName = 'ToolbarDivider'

/**
 * Toolbar - formatting toolbar for text editing
 * Uses Chakra UI Popover for color pickers
 */
export const Toolbar: React.FC<ToolbarProps> = ({ editor, isRtl = false }) => {
  const [textColorPickerOpen, setTextColorPickerOpen] = useState(false)
  const [highlightColorPickerOpen, setHighlightColorPickerOpen] = useState(false)

  // Handlers for color pickers
  const toggleTextColorPicker = useCallback(() => {
    setTextColorPickerOpen((prev) => !prev)
    setHighlightColorPickerOpen(false)
  }, [])

  const toggleHighlightColorPicker = useCallback(() => {
    setHighlightColorPickerOpen((prev) => !prev)
    setTextColorPickerOpen(false)
  }, [])

  const closeTextColorPicker = useCallback(() => setTextColorPickerOpen(false), [])
  const closeHighlightColorPicker = useCallback(() => setHighlightColorPickerOpen(false), [])

  // Text formatting commands
  const toggleBold = useCallback(() => editor.chain().focus().toggleBold().run(), [editor])
  const toggleItalic = useCallback(() => editor.chain().focus().toggleItalic().run(), [editor])
  const toggleUnderline = useCallback(() => editor.chain().focus().toggleUnderline().run(), [editor])
  const toggleStrike = useCallback(() => editor.chain().focus().toggleStrike().run(), [editor])
  // const toggleCode = useCallback(() => editor.chain().focus().toggleCode().run(), [editor])

  // Heading commands
  const toggleHeading1 = useCallback(() => editor.chain().focus().toggleHeading({ level: 1 }).run(), [editor])
  const toggleHeading2 = useCallback(() => editor.chain().focus().toggleHeading({ level: 2 }).run(), [editor])
  const toggleHeading3 = useCallback(() => editor.chain().focus().toggleHeading({ level: 3 }).run(), [editor])

  // List commands
  const toggleBulletList = useCallback(() => editor.chain().focus().toggleBulletList().run(), [editor])
  const toggleOrderedList = useCallback(() => editor.chain().focus().toggleOrderedList().run(), [editor])
  // const toggleBlockquote = useCallback(() => editor.chain().focus().toggleBlockquote().run(), [editor])

  // Alignment commands
  // const alignLeft = useCallback(() => editor.chain().focus().setTextAlign('left').run(), [editor])
  // const alignCenter = useCallback(() => editor.chain().focus().setTextAlign('center').run(), [editor])
  // const alignRight = useCallback(() => editor.chain().focus().setTextAlign('right').run(), [editor])
  // const alignJustify = useCallback(() => editor.chain().focus().setTextAlign('justify').run(), [editor])

  // Undo/redo commands
  const undo = useCallback(() => editor.chain().focus().undo().run(), [editor])
  const redo = useCallback(() => editor.chain().focus().redo().run(), [editor])

  if (!editor) {
    return null
  }

  return (
    <Flex className={`rich-text-toolbar ${isRtl ? 'rtl' : 'ltr'}`} wrap="nowrap">
      {/* Text formatting */}
      <ToolbarButton onClick={toggleBold} isActive={editor.isActive('bold')} title="Bold (Ctrl+B)">
        <Bold size={18} />
      </ToolbarButton>

      <ToolbarButton onClick={toggleItalic} isActive={editor.isActive('italic')} title="Italic (Ctrl+I)">
        <Italic size={18} />
      </ToolbarButton>

      <ToolbarButton onClick={toggleUnderline} isActive={editor.isActive('underline')} title="Underline (Ctrl+U)">
        <UnderlineIcon size={18} />
      </ToolbarButton>

      <ToolbarButton onClick={toggleStrike} isActive={editor.isActive('strike')} title="Strikethrough">
        <Strikethrough size={18} />
      </ToolbarButton>

      {/* <ToolbarButton onClick={toggleCode} isActive={editor.isActive('code')} title="Code">
        <Code size={18} />
      </ToolbarButton> */}

      <ToolbarDivider />

      {/* Headings */}
      <ToolbarButton onClick={toggleHeading1} isActive={editor.isActive('heading', { level: 1 })} title="Heading 1">
        <Heading1 size={18} />
      </ToolbarButton>

      <ToolbarButton onClick={toggleHeading2} isActive={editor.isActive('heading', { level: 2 })} title="Heading 2">
        <Heading2 size={18} />
      </ToolbarButton>

      <ToolbarButton onClick={toggleHeading3} isActive={editor.isActive('heading', { level: 3 })} title="Heading 3">
        <Heading3 size={18} />
      </ToolbarButton>

      <ToolbarDivider />

      {/* Lists */}
      <ToolbarButton onClick={toggleBulletList} isActive={editor.isActive('bulletList')} title="Bullet List">
        <List size={18} />
      </ToolbarButton>

      <ToolbarButton onClick={toggleOrderedList} isActive={editor.isActive('orderedList')} title="Numbered List">
        <ListOrdered size={18} />
      </ToolbarButton>

      {/* <ToolbarButton onClick={toggleBlockquote} isActive={editor.isActive('blockquote')} title="Blockquote">
        <Quote size={18} />
      </ToolbarButton> */}

      <ToolbarDivider />

      {/* Text alignment */}
      {/* <ToolbarButton onClick={alignLeft} isActive={editor.isActive({ textAlign: 'left' })} title="Align Left">
        <AlignLeft size={18} />
      </ToolbarButton>

      <ToolbarButton onClick={alignCenter} isActive={editor.isActive({ textAlign: 'center' })} title="Align Center">
        <AlignCenter size={18} />
      </ToolbarButton>

      <ToolbarButton onClick={alignRight} isActive={editor.isActive({ textAlign: 'right' })} title="Align Right">
        <AlignRight size={18} />
      </ToolbarButton>

      <ToolbarButton onClick={alignJustify} isActive={editor.isActive({ textAlign: 'justify' })} title="Justify">
        <AlignJustify size={18} />
      </ToolbarButton> */}

      {/* <ToolbarDivider /> */}

      {/* Text color with Chakra UI Popover */}
      <ColorPicker
        editor={editor}
        type="text"
        isOpen={textColorPickerOpen}
        onClose={closeTextColorPicker}
        onToggle={toggleTextColorPicker}
        isActive={!!editor.getAttributes('textStyle').color}
      >
        <Type size={18} />
      </ColorPicker>

      {/* Highlight color with Chakra UI Popover */}
      <ColorPicker
        editor={editor}
        type="highlight"
        isOpen={highlightColorPickerOpen}
        onClose={closeHighlightColorPicker}
        onToggle={toggleHighlightColorPicker}
        isActive={editor.isActive('highlight')}
      >
        <Highlighter size={18} />
      </ColorPicker>

      <ToolbarDivider />

      {/* Undo/Redo */}
      <ToolbarButton onClick={undo} disabled={!editor.can().undo()} title="Undo (Ctrl+Z)">
        <Undo size={18} />
      </ToolbarButton>

      <ToolbarButton onClick={redo} disabled={!editor.can().redo()} title="Redo (Ctrl+Y)">
        <Redo size={18} />
      </ToolbarButton>
    </Flex>
  )
}
